import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import API from "../../../lib/api";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../ui/dialog";

/** Platform-wide payment instructions printed on every bespoke-module invoice PDF (seller name / VAT come from the Revolut receipt settings). */
export function InvoiceSettingsDialog({ open, onOpenChange }) {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) API.get("/platform/bespoke/invoice-settings").then((r) => setS(r.data)).catch(() => toast.error("Couldn't load invoice settings")); }, [open]);
  const save = async () => {
    setBusy(true);
    try { const { data } = await API.put("/platform/bespoke/invoice-settings", s); setS(data); toast.success("Invoice settings saved"); onOpenChange(false); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't save"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="bespoke-invoice-settings-dialog">
        <DialogHeader><DialogTitle className="font-[Chivo]">Invoice settings</DialogTitle><DialogDescription>Printed on every bespoke-module invoice PDF (numbered BMI-000001…). Your legal name, address and VAT rate come from the Revolut receipt settings.</DialogDescription></DialogHeader>
        {!s ? <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p> : (
          <div className="space-y-3">
            <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-xs" data-testid="bespoke-invoice-seller">
              <p className="font-semibold">{s.seller?.legal_name || "No legal name set"}</p>
              <p className="text-muted-foreground whitespace-pre-line">{s.seller?.address || "No address"}{s.seller?.vat_number ? ` · VAT ${s.seller.vat_number}` : ""}{s.seller?.vat_rate ? ` · VAT ${s.seller.vat_rate}% included` : " · no VAT"}</p>
              <Link to="/platform/subscription-plans" className="text-primary hover:underline">Edit seller details (Subscription plans → Revolut → receipts) →</Link>
            </div>
            <div><Label className="text-xs">How tenants pay</Label><Textarea rows={5} value={s.payment_instructions} onChange={(e) => setS((x) => ({ ...x, payment_instructions: e.target.value }))} placeholder={"Bank transfer to:\nFixlr Ltd · Sort code 00-00-00 · Account 12345678\nRemittances to accounts@fixlr.io"} className="mt-1" data-testid="bespoke-invoice-payment-instructions" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Payment terms (days)</Label><Input type="number" min="0" max="90" value={s.terms_days} onChange={(e) => setS((x) => ({ ...x, terms_days: e.target.value }))} className="mt-1" data-testid="bespoke-invoice-terms" /></div>
              <div><Label className="text-xs">Footer note</Label><Input value={s.footer_note} onChange={(e) => setS((x) => ({ ...x, footer_note: e.target.value }))} maxLength={300} className="mt-1" data-testid="bespoke-invoice-footer" /></div>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={busy || !s} data-testid="bespoke-invoice-settings-save">{busy ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
